import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download, Plus, Trash2, Upload, RotateCcw, FileImage, Save, Copy, Printer, ZoomIn, Move, Share2, Check, Sun, Moon, ChevronDown, ChevronUp, Undo2, Redo2, GripVertical, Package } from "lucide-react";

const STORAGE_KEY = "kill-team-datacard-generator-v3";
const CARD_W_MM = 63;
const CARD_H_MM = 88;
const SHEET_GAP_MM = 6;

const defaultTheme = {
  headerBg: "#141414",
  headerAccent: "#c84420",
  panelBg: "#1a1a1a",
  border: "#2a2a2a",
  textDark: "#ffffff",
  frameInset: "#222222",
  innerPanel: "#1a1a1a",
  mutedLine: "#333333",
  cardBg: "#1a1a1a",
  textLight: "#ffffff",
  textMuted: "#888888",
  chevronColor: "#222222",
  accentBar: "#c84420",
  statBg: "#242424",
};

const lightTheme = {
  headerBg: "#1b2128",
  headerAccent: "#c84420",
  panelBg: "#ffffff",
  border: "#e2e2e2",
  textDark: "#1a1a1a",
  frameInset: "#f0f0f0",
  innerPanel: "#ffffff",
  mutedLine: "#e8e8e8",
  cardBg: "#ffffff",
  textLight: "#1a1a1a",
  textMuted: "#666666",
  chevronColor: "#f0f0f0",
  accentBar: "#c84420",
  statBg: "#f7f7f7",
};

// Keys that are accent/faction-specific — preserved from card theme when toggling
const ACCENT_KEYS = ["headerAccent", "accentBar", "headerBg"];

function applyModeTheme(card, isDark) {
  const base = isDark ? defaultTheme : lightTheme;
  const cardTheme = card.theme || {};
  // Use the base (dark/light) for all structural colors, but keep faction accent colors from the card
  const merged = { ...base };
  for (const key of ACCENT_KEYS) {
    if (cardTheme[key]) merged[key] = cardTheme[key];
  }
  return { ...card, theme: merged };
}

// ── Shareable link encoding (compression → base64url in hash fragment) ──

function toBase64Url(bytes) {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(str) {
  const padded = str.replace(/-/g, "+").replace(/_/g, "/") + "===".slice(0, (4 - (str.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function compressString(str) {
  const encoded = new TextEncoder().encode(str);
  if (typeof CompressionStream !== "undefined") {
    const cs = new CompressionStream("deflate-raw");
    const writer = cs.writable.getWriter();
    writer.write(encoded);
    writer.close();
    const chunks = [];
    const reader = cs.readable.getReader();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }
    const total = chunks.reduce((a, c) => a + c.length, 0);
    const result = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) { result.set(chunk, offset); offset += chunk.length; }
    return "1" + toBase64Url(result); // "1" prefix = compressed
  }
  return "0" + toBase64Url(encoded); // "0" prefix = raw
}

async function decompressString(data) {
  const flag = data[0];
  const payload = fromBase64Url(data.slice(1));
  if (flag === "1" && typeof DecompressionStream !== "undefined") {
    const ds = new DecompressionStream("deflate-raw");
    const writer = ds.writable.getWriter();
    writer.write(payload);
    writer.close();
    const chunks = [];
    const reader = ds.readable.getReader();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }
    const total = chunks.reduce((a, c) => a + c.length, 0);
    const result = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) { result.set(chunk, offset); offset += chunk.length; }
    return new TextDecoder().decode(result);
  }
  return new TextDecoder().decode(payload);
}

function makeShareableCard(card) {
  const shareable = deepClone(card);
  // Strip large data URIs — they'd make the URL impossibly long
  if (shareable.portraitUrl && shareable.portraitUrl.startsWith("data:")) {
    shareable.portraitUrl = "";
  }
  // Remove the id — recipient gets a fresh one
  delete shareable.id;
  return shareable;
}

const PORTRAIT_PLACEHOLDER = "data:image/svg+xml," + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="500" height="500" viewBox="0 0 500 500">' +
  '<rect width="500" height="500" fill="#2a2a2a"/>' +
  '<circle cx="250" cy="190" r="70" fill="none" stroke="#444" stroke-width="4"/>' +
  '<path d="M160,380 Q170,280 250,280 Q330,280 340,380" fill="none" stroke="#444" stroke-width="4"/>' +
  '<text x="250" y="440" text-anchor="middle" font-family="Arial,sans-serif" font-size="22" fill="#555">Add Portrait</text>' +
  '</svg>'
);

// ── Weapon Special Rules Tooltips ──
const WEAPON_RULES = {
  "Ceaseless": "Each time a friendly operative performs a Shoot action with this weapon, you can re-roll any or all of your attack dice results of 1.",
  "Heavy": "An operative cannot perform a Shoot action in the same activation in which it performed a Normal Move, Fall Back, or Charge action.",
  "Hot": "Each time a friendly operative performs a Shoot action, for each attack dice result of 1, that friendly operative suffers 3 mortal wounds.",
  "Lethal 5+": "Each time a friendly operative performs a Shoot or Fight action, attack dice results of 5+ that are successful are critical hits.",
  "Brutal": "Your opponent cannot parry with normal hits, only critical hits.",
  "Rending": "If you retain any critical hits, you can re-roll one of your failed hits as a successful normal hit.",
  "Stun": "Each time you strike with a critical hit, subtract 1 from the target's APL.",
  "AP1": "Each time you strike with a critical hit, the target's Save characteristic worsens by 1 for that strike.",
  "AP2": "Each time you strike, the target's Save characteristic worsens by 2 for that strike.",
  "P1": "Each time you strike, the target's Save worsens by 1 for that strike.",
  "Torrent": "Each time a friendly operative performs a Shoot action, you can re-roll any or all of your attack dice.",
  "Blast": "If you retain any critical hits, inflict damage on each other operative Visible to and within 2\" of the original target.",
  "MW": "Each time you strike with a critical hit, inflict mortal wounds instead of normal damage.",
  "Relentless": "Each time a friendly operative performs a Shoot or Fight action, you can re-roll any or all of your attack dice.",
  "Reap": "Each time a friendly operative fights in combat, if you strike with a critical hit, inflict damage on each other valid target within the specified range.",
  "Special": "This weapon has a special rule — refer to the operative's datacard for details.",
};

// ── Weapons Library ──
const WEAPON_LIBRARY = [
  // IMPERIUM – SPACE MARINES
  { faction: "Space Marines", type: "Ranged", name: "Bolt Pistol", atk: "4", hit: "3+", dmg: "3/4", wr: 'Range 8"' },
  { faction: "Space Marines", type: "Ranged", name: "Heavy Bolt Pistol", atk: "4", hit: "3+", dmg: "4/5", wr: 'Range 8"' },
  { faction: "Space Marines", type: "Ranged", name: "Bolt Rifle", atk: "4", hit: "3+", dmg: "3/4", wr: 'Range 12"' },
  { faction: "Space Marines", type: "Ranged", name: "Auto Bolt Rifle", atk: "5", hit: "3+", dmg: "3/4", wr: "Ceaseless" },
  { faction: "Space Marines", type: "Ranged", name: "Stalker Bolt Rifle", atk: "4", hit: "3+", dmg: "4/5", wr: "Heavy" },
  { faction: "Space Marines", type: "Ranged", name: "Storm Bolter", atk: "5", hit: "3+", dmg: "3/4", wr: "Ceaseless" },
  { faction: "Space Marines", type: "Ranged", name: "Heavy Bolter", atk: "5", hit: "3+", dmg: "4/5", wr: "P1, Heavy" },
  { faction: "Space Marines", type: "Ranged", name: "Plasma Pistol", atk: "4", hit: "3+", dmg: "5/6", wr: "AP1, Hot" },
  { faction: "Space Marines", type: "Ranged", name: "Plasma Gun", atk: "4", hit: "3+", dmg: "5/6", wr: "AP1, Hot" },
  { faction: "Space Marines", type: "Ranged", name: "Melta Gun", atk: "4", hit: "3+", dmg: "6/7", wr: 'AP2, Range 6"' },
  { faction: "Space Marines", type: "Ranged", name: "Grav Gun", atk: "4", hit: "3+", dmg: "4/5", wr: "AP1" },
  { faction: "Space Marines", type: "Ranged", name: "Flamer", atk: "5", hit: "2+", dmg: "2/3", wr: 'Torrent 1"' },
  { faction: "Space Marines", type: "Ranged", name: "Combi-weapon", atk: "4", hit: "3+", dmg: "3/4", wr: "Special" },
  { faction: "Space Marines", type: "Melee", name: "Chainsword", atk: "5", hit: "3+", dmg: "4/5", wr: "Brutal" },
  { faction: "Space Marines", type: "Melee", name: "Power Sword", atk: "5", hit: "3+", dmg: "5/7", wr: "Lethal 5+" },
  { faction: "Space Marines", type: "Melee", name: "Power Fist", atk: "4", hit: "4+", dmg: "7/9", wr: "Brutal" },
  { faction: "Space Marines", type: "Melee", name: "Thunder Hammer", atk: "4", hit: "4+", dmg: "7/9", wr: "Stun" },
  { faction: "Space Marines", type: "Melee", name: "Lightning Claws", atk: "5", hit: "3+", dmg: "4/5", wr: "Rending" },
  { faction: "Space Marines", type: "Melee", name: "Relic Blade", atk: "5", hit: "3+", dmg: "6/7", wr: "" },
  { faction: "Space Marines", type: "Melee", name: "Combat Knife", atk: "4", hit: "3+", dmg: "3/4", wr: "" },
  // CHAOS SPACE MARINES
  { faction: "Chaos", type: "Ranged", name: "Boltgun", atk: "4", hit: "3+", dmg: "3/4", wr: "" },
  { faction: "Chaos", type: "Ranged", name: "Plasma Gun", atk: "4", hit: "3+", dmg: "5/6", wr: "AP1, Hot" },
  { faction: "Chaos", type: "Ranged", name: "Reaper Chaincannon", atk: "6", hit: "3+", dmg: "3/4", wr: "Ceaseless" },
  { faction: "Chaos", type: "Ranged", name: "Flamer", atk: "5", hit: "2+", dmg: "2/3", wr: "Torrent" },
  { faction: "Chaos", type: "Ranged", name: "Meltagun", atk: "4", hit: "3+", dmg: "6/7", wr: "AP2" },
  { faction: "Chaos", type: "Ranged", name: "Autocannon", atk: "5", hit: "4+", dmg: "4/5", wr: "" },
  { faction: "Chaos", type: "Melee", name: "Chainsword", atk: "5", hit: "3+", dmg: "4/5", wr: "Brutal" },
  { faction: "Chaos", type: "Melee", name: "Accursed Weapon", atk: "5", hit: "3+", dmg: "5/6", wr: "" },
  { faction: "Chaos", type: "Melee", name: "Power Axe", atk: "5", hit: "3+", dmg: "5/7", wr: "" },
  { faction: "Chaos", type: "Melee", name: "Daemon Blade", atk: "5", hit: "3+", dmg: "6/7", wr: "Lethal 5+" },
  // DEATH GUARD
  { faction: "Death Guard", type: "Ranged", name: "Plague Spewer", atk: "5", hit: "2+", dmg: "2/3", wr: "Torrent" },
  { faction: "Death Guard", type: "Ranged", name: "Blight Launcher", atk: "4", hit: "3+", dmg: "4/5", wr: "AP1" },
  { faction: "Death Guard", type: "Ranged", name: "Plasma Gun", atk: "4", hit: "3+", dmg: "5/6", wr: "AP1" },
  { faction: "Death Guard", type: "Melee", name: "Plague Knife", atk: "4", hit: "3+", dmg: "3/4", wr: "Lethal 5+" },
  { faction: "Death Guard", type: "Melee", name: "Bubotic Axe", atk: "5", hit: "3+", dmg: "5/7", wr: "" },
  { faction: "Death Guard", type: "Melee", name: "Flail of Corruption", atk: "5", hit: "3+", dmg: "4/5", wr: 'Reap 2"' },
  // ASTRA MILITARUM
  { faction: "Astra Militarum", type: "Ranged", name: "Lasgun", atk: "4", hit: "4+", dmg: "2/3", wr: "" },
  { faction: "Astra Militarum", type: "Ranged", name: "Hot-shot Lasgun", atk: "4", hit: "3+", dmg: "3/4", wr: "AP1" },
  { faction: "Astra Militarum", type: "Ranged", name: "Plasma Gun", atk: "4", hit: "4+", dmg: "5/6", wr: "AP1, Hot" },
  { faction: "Astra Militarum", type: "Ranged", name: "Meltagun", atk: "4", hit: "4+", dmg: "6/7", wr: "AP2" },
  { faction: "Astra Militarum", type: "Ranged", name: "Grenade Launcher", atk: "4", hit: "4+", dmg: "3/4", wr: "Blast" },
  { faction: "Astra Militarum", type: "Ranged", name: "Sniper Rifle", atk: "4", hit: "3+", dmg: "3/4", wr: "MW" },
  { faction: "Astra Militarum", type: "Melee", name: "Bayonet", atk: "3", hit: "4+", dmg: "2/3", wr: "" },
  { faction: "Astra Militarum", type: "Melee", name: "Chainsword", atk: "4", hit: "4+", dmg: "3/4", wr: "" },
  // TYRANIDS
  { faction: "Tyranids", type: "Ranged", name: "Fleshborer", atk: "4", hit: "4+", dmg: "3/4", wr: "" },
  { faction: "Tyranids", type: "Ranged", name: "Devourer", atk: "5", hit: "4+", dmg: "3/4", wr: "" },
  { faction: "Tyranids", type: "Ranged", name: "Deathspitter", atk: "4", hit: "3+", dmg: "4/5", wr: "" },
  { faction: "Tyranids", type: "Ranged", name: "Venom Cannon", atk: "4", hit: "3+", dmg: "5/6", wr: "Blast" },
  { faction: "Tyranids", type: "Melee", name: "Scything Talons", atk: "5", hit: "3+", dmg: "4/5", wr: "Ceaseless" },
  { faction: "Tyranids", type: "Melee", name: "Rending Claws", atk: "5", hit: "3+", dmg: "4/5", wr: "Rending" },
  { faction: "Tyranids", type: "Melee", name: "Bonesword", atk: "5", hit: "3+", dmg: "5/7", wr: "" },
  // ORKS
  { faction: "Orks", type: "Ranged", name: "Shoota", atk: "5", hit: "4+", dmg: "3/4", wr: "" },
  { faction: "Orks", type: "Ranged", name: "Slugga", atk: "4", hit: "4+", dmg: "3/4", wr: "" },
  { faction: "Orks", type: "Ranged", name: "Big Shoota", atk: "5", hit: "4+", dmg: "4/5", wr: "" },
  { faction: "Orks", type: "Ranged", name: "Rokkit Launcha", atk: "4", hit: "5+", dmg: "6/7", wr: "AP2" },
  { faction: "Orks", type: "Melee", name: "Choppa", atk: "5", hit: "3+", dmg: "4/5", wr: "" },
  { faction: "Orks", type: "Melee", name: "Power Klaw", atk: "4", hit: "4+", dmg: "7/9", wr: "Brutal" },
  { faction: "Orks", type: "Melee", name: "Big Choppa", atk: "5", hit: "3+", dmg: "5/6", wr: "" },
  // ADEPTUS MECHANICUS
  { faction: "Ad Mech", type: "Ranged", name: "Radium Carbine", atk: "5", hit: "3+", dmg: "3/4", wr: "Lethal 5+" },
  { faction: "Ad Mech", type: "Ranged", name: "Arc Rifle", atk: "4", hit: "3+", dmg: "4/5", wr: "AP1" },
  { faction: "Ad Mech", type: "Ranged", name: "Galvanic Rifle", atk: "4", hit: "3+", dmg: "3/4", wr: "" },
  { faction: "Ad Mech", type: "Melee", name: "Arc Maul", atk: "5", hit: "3+", dmg: "5/6", wr: "" },
  { faction: "Ad Mech", type: "Melee", name: "Transonic Blade", atk: "5", hit: "3+", dmg: "5/7", wr: "" },
  // TAU EMPIRE
  { faction: "Tau", type: "Ranged", name: "Pulse Rifle", atk: "4", hit: "3+", dmg: "4/5", wr: "" },
  { faction: "Tau", type: "Ranged", name: "Pulse Carbine", atk: "4", hit: "3+", dmg: "3/4", wr: "" },
  { faction: "Tau", type: "Ranged", name: "Pulse Blaster", atk: "5", hit: "3+", dmg: "4/5", wr: 'Range 6"' },
  { faction: "Tau", type: "Ranged", name: "Pulse Pistol", atk: "4", hit: "3+", dmg: "3/4", wr: 'Range 6"' },
  { faction: "Tau", type: "Ranged", name: "Rail Rifle", atk: "4", hit: "3+", dmg: "5/7", wr: "AP2, Heavy" },
  { faction: "Tau", type: "Ranged", name: "Ion Rifle", atk: "4", hit: "3+", dmg: "5/6", wr: "AP1" },
  { faction: "Tau", type: "Ranged", name: "Missile Pod", atk: "4", hit: "3+", dmg: "5/6", wr: "AP1" },
  { faction: "Tau", type: "Melee", name: "Close Combat Weapon", atk: "3", hit: "5+", dmg: "2/3", wr: "" },
  { faction: "Tau", type: "Melee", name: "Bonding Knife", atk: "3", hit: "4+", dmg: "2/3", wr: "" },
  // NECRONS
  { faction: "Necrons", type: "Ranged", name: "Gauss Flayer", atk: "4", hit: "3+", dmg: "3/4", wr: "" },
  { faction: "Necrons", type: "Ranged", name: "Gauss Reaper", atk: "5", hit: "3+", dmg: "3/4", wr: 'Range 6"' },
  { faction: "Necrons", type: "Ranged", name: "Gauss Blaster", atk: "4", hit: "3+", dmg: "5/6", wr: "AP1" },
  { faction: "Necrons", type: "Ranged", name: "Tesla Carbine", atk: "4", hit: "3+", dmg: "3/4", wr: "Ceaseless" },
  { faction: "Necrons", type: "Ranged", name: "Synaptic Disintegrator", atk: "4", hit: "2+", dmg: "3/4", wr: "Heavy, MW" },
  { faction: "Necrons", type: "Melee", name: "Bayonet", atk: "3", hit: "3+", dmg: "3/4", wr: "" },
  { faction: "Necrons", type: "Melee", name: "Hyperphase Sword", atk: "4", hit: "3+", dmg: "5/6", wr: "Lethal 5+" },
  { faction: "Necrons", type: "Melee", name: "Warscythe", atk: "4", hit: "3+", dmg: "6/8", wr: "Brutal" },
  // DRUKHARI
  { faction: "Drukhari", type: "Ranged", name: "Splinter Rifle", atk: "4", hit: "3+", dmg: "2/3", wr: "Lethal 5+" },
  { faction: "Drukhari", type: "Ranged", name: "Splinter Pistol", atk: "4", hit: "3+", dmg: "2/3", wr: 'Lethal 5+, Range 6"' },
  { faction: "Drukhari", type: "Ranged", name: "Shredder", atk: "5", hit: "3+", dmg: "3/5", wr: "Rending" },
  { faction: "Drukhari", type: "Ranged", name: "Blaster", atk: "4", hit: "3+", dmg: "6/7", wr: "AP2" },
  { faction: "Drukhari", type: "Ranged", name: "Dark Lance", atk: "4", hit: "3+", dmg: "6/8", wr: "AP2, Heavy" },
  { faction: "Drukhari", type: "Melee", name: "Power Sword", atk: "4", hit: "3+", dmg: "5/6", wr: "Lethal 5+" },
  { faction: "Drukhari", type: "Melee", name: "Agoniser", atk: "4", hit: "3+", dmg: "4/5", wr: "Lethal 5+" },
  { faction: "Drukhari", type: "Melee", name: "Wych Knife", atk: "4", hit: "3+", dmg: "3/4", wr: "" },
  // ADEPTA SORORITAS
  { faction: "Sisters", type: "Ranged", name: "Boltgun", atk: "4", hit: "3+", dmg: "3/4", wr: "" },
  { faction: "Sisters", type: "Ranged", name: "Bolt Pistol", atk: "4", hit: "3+", dmg: "3/4", wr: 'Range 8"' },
  { faction: "Sisters", type: "Ranged", name: "Storm Bolter", atk: "5", hit: "3+", dmg: "3/4", wr: "Ceaseless" },
  { faction: "Sisters", type: "Ranged", name: "Flamer", atk: "5", hit: "2+", dmg: "2/3", wr: "Torrent" },
  { faction: "Sisters", type: "Ranged", name: "Meltagun", atk: "4", hit: "3+", dmg: "6/7", wr: 'AP2, Range 6"' },
  { faction: "Sisters", type: "Ranged", name: "Inferno Pistol", atk: "4", hit: "3+", dmg: "5/6", wr: 'AP2, Range 6"' },
  { faction: "Sisters", type: "Melee", name: "Chainsword", atk: "5", hit: "3+", dmg: "4/5", wr: "Brutal" },
  { faction: "Sisters", type: "Melee", name: "Power Weapon", atk: "5", hit: "3+", dmg: "5/6", wr: "Lethal 5+" },
  { faction: "Sisters", type: "Melee", name: "Hallowed Mace", atk: "4", hit: "3+", dmg: "5/7", wr: "Stun" },
];

const WEAPON_FACTIONS = [...new Set(WEAPON_LIBRARY.map((w) => w.faction))];

function sanitizePortraitUrl(url) {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (trimmed.startsWith("data:image/")) return trimmed;
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol === "https:" || parsed.protocol === "http:") return trimmed;
  } catch {}
  return "";
}

function sanitizeString(val, maxLen = 500) {
  if (typeof val !== "string") return "";
  return val.slice(0, maxLen);
}

function sanitizeColor(val) {
  if (typeof val !== "string") return undefined;
  if (/^#[0-9a-fA-F]{3,8}$/.test(val)) return val;
  return undefined;
}

function validateCardData(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const card = {};
  card.title = sanitizeString(raw.title, 100);
  card.subtitle = sanitizeString(raw.subtitle, 100);
  card.factionIcon = sanitizeString(raw.factionIcon, 10);
  card.portraitUrl = sanitizePortraitUrl(raw.portraitUrl);
  card.portraitFit = ["cover", "contain", "fill"].includes(raw.portraitFit) ? raw.portraitFit : "cover";
  card.portraitZoom = typeof raw.portraitZoom === "number" ? Math.max(1, Math.min(3, raw.portraitZoom)) : 1;
  card.portraitOffsetX = typeof raw.portraitOffsetX === "number" ? Math.max(-100, Math.min(100, raw.portraitOffsetX)) : 0;
  card.portraitOffsetY = typeof raw.portraitOffsetY === "number" ? Math.max(-100, Math.min(100, raw.portraitOffsetY)) : 0;
  if (raw.stats && typeof raw.stats === "object" && !Array.isArray(raw.stats)) {
    card.stats = {
      apl: sanitizeString(raw.stats.apl, 10),
      move: sanitizeString(raw.stats.move, 10),
      save: sanitizeString(raw.stats.save, 10),
      wounds: sanitizeString(raw.stats.wounds, 10),
    };
  }
  if (Array.isArray(raw.weapons)) {
    card.weapons = raw.weapons.slice(0, 20).map((w) => {
      if (!w || typeof w !== "object") return null;
      return {
        name: sanitizeString(w.name, 100),
        atk: sanitizeString(w.atk, 10),
        hit: sanitizeString(w.hit, 10),
        dmg: sanitizeString(w.dmg, 20),
        wr: sanitizeString(w.wr, 200),
      };
    }).filter(Boolean);
  }
  card.equipmentTitle = sanitizeString(raw.equipmentTitle, 100);
  if (Array.isArray(raw.equipmentBullets)) {
    card.equipmentBullets = raw.equipmentBullets.slice(0, 20).map((b) => sanitizeString(b, 500)).filter(Boolean);
  }
  if (Array.isArray(raw.abilities)) {
    card.abilities = raw.abilities.slice(0, 20).map((a) => {
      if (!a || typeof a !== "object") return null;
      return { title: sanitizeString(a.title, 100), body: sanitizeString(a.body, 1000) };
    }).filter(Boolean);
  }
  card.uniqueActionTitle = sanitizeString(raw.uniqueActionTitle, 100);
  card.uniqueActionAp = sanitizeString(raw.uniqueActionAp, 20);
  card.uniqueActionBody = sanitizeString(raw.uniqueActionBody, 1000);
  card.keywords = sanitizeString(raw.keywords, 300);
  if (raw.theme && typeof raw.theme === "object" && !Array.isArray(raw.theme)) {
    card.theme = {};
    for (const key of Object.keys(defaultTheme)) {
      card.theme[key] = sanitizeColor(raw.theme[key]) || defaultTheme[key];
    }
  }
  return card;
}

function uid() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `card-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const defaultCard = {
  id: uid(),
  title: "CAPTAIN TITUS",
  subtitle: "ULTRAMARINES CAPTAIN",
  factionIcon: "Ω",
  portraitUrl: "",
  portraitFit: "cover",
  portraitZoom: 1,
  portraitOffsetX: 0,
  portraitOffsetY: 0,
  stats: {
    apl: "3",
    move: '6"',
    save: "3+",
    wounds: "17",
  },
  weapons: [
    { name: "Master-crafted Bolt Pistol", atk: "4", hit: "3+", dmg: "3/4", wr: 'Range 8\", Lethal 5+' },
    { name: "Chainsword of Graia", atk: "5", hit: "2+", dmg: "4/5", wr: "Brutal, Lethal 5+" },
  ],
  equipmentTitle: "CAPTAIN'S IRON HALO",
  equipmentBullets: ["This operative has a 4+ invulnerable save."],
  abilities: [
    {
      title: "Veteran of Graia",
      body: "Each time this operative performs a Fight or Shoot action, you may re-roll one attack dice.",
    },
    {
      title: "Adaptive Combat Doctrine",
      body: "Once per Turning Point, when this operative performs a Fight or Shoot action, choose one: Aggression - retain one normal hit as a critical hit; Precision - change one failed hit to a normal hit.",
    },
  ],
  uniqueActionTitle: "FOR THE EMPEROR!",
  uniqueActionAp: "AP 1",
  uniqueActionBody:
    "Select one friendly ULTRAMARINES operative within 6\". Until the end of the Turning Point: that operative gains +1 APL; the first time it performs a Shoot or Fight action, it may re-roll one attack dice.",
  keywords: "IMPERIUM · ADEPTUS ASTARTES · ULTRAMARINES · CAPTAIN · LEADER",
  theme: defaultTheme,
};

const TEMPLATES = [
  {
    label: "Space Marine",
    icon: "",
    data: {
      title: "INTERCESSOR SERGEANT",
      subtitle: "SPACE MARINES WARRIOR",
      factionIcon: "",
      stats: { apl: "3", move: '6"', save: "3+", wounds: "13" },
      weapons: [
        { name: "Bolt Rifle", atk: "4", hit: "3+", dmg: "3/4", wr: 'Range 12", Ceaseless' },
        { name: "Power Weapon", atk: "4", hit: "3+", dmg: "4/5", wr: "Lethal 5+" },
      ],
      equipmentTitle: "AUSPEX",
      equipmentBullets: ["Enemy operatives cannot be in Cover within 6\" of this operative."],
      abilities: [{ title: "Transhuman Physiology", body: "Each time this operative would lose a wound, roll one D6: on a 6, that wound is not lost." }],
      uniqueActionTitle: "TACTICAL ORDERS",
      uniqueActionAp: "AP 1",
      uniqueActionBody: "Select one friendly SPACE MARINES operative within 6\". That operative may immediately perform a free Dash action.",
      keywords: "IMPERIUM \u00B7 ADEPTUS ASTARTES \u00B7 SPACE MARINES \u00B7 INTERCESSOR \u00B7 LEADER",
      theme: { ...defaultTheme, headerAccent: "#1a5fc1", accentBar: "#1a5fc1", headerBg: "#0e1520" },
    },
  },
  {
    label: "Death Guard",
    icon: "",
    data: {
      title: "SERGEANT VETERAN",
      subtitle: "IMPERIAL GUARD LEADER",
      factionIcon: "",
      stats: { apl: "3", move: '6"', save: "5+", wounds: "8" },
      weapons: [
        { name: "Lasgun", atk: "4", hit: "4+", dmg: "2/3", wr: 'Range 12", Ceaseless' },
        { name: "Bayonet", atk: "3", hit: "4+", dmg: "2/3", wr: "" },
      ],
      equipmentTitle: "FRAG GRENADE",
      equipmentBullets: ["Once per battle, this operative can use a frag grenade: 4 ATK, 4+ HIT, 2/3 DMG, Range 6\", Blast 2\"."],
      abilities: [{ title: "Voice of Command", body: "Once per Turning Point, select one friendly IMPERIAL GUARD operative within 6\". That operative gains +1 APL until the end of the Turning Point." }],
      uniqueActionTitle: "ISSUE ORDER",
      uniqueActionAp: "AP 1",
      uniqueActionBody: "Select one friendly IMPERIAL GUARD operative within 6\". That operative may immediately perform a free Dash or Fall Back action.",
      keywords: "IMPERIUM \u00B7 ASTRA MILITARUM \u00B7 IMPERIAL GUARD \u00B7 VETERAN \u00B7 LEADER",
      theme: { ...defaultTheme, headerAccent: "#6b8e23", accentBar: "#6b8e23", headerBg: "#151a10" },
    },
  },
  {
    label: "Chaos",
    icon: "",
    data: {
      title: "ASPIRING CHAMPION",
      subtitle: "CHAOS LEGIONARY LEADER",
      factionIcon: "",
      stats: { apl: "3", move: '6"', save: "3+", wounds: "14" },
      weapons: [
        { name: "Bolt Pistol", atk: "4", hit: "3+", dmg: "3/4", wr: 'Range 8"' },
        { name: "Accursed Weapon", atk: "5", hit: "3+", dmg: "4/6", wr: "Brutal, Lethal 5+" },
      ],
      equipmentTitle: "MARK OF KHORNE",
      equipmentBullets: ["Each time this operative fights in combat, you may re-roll any or all of your attack dice."],
      abilities: [{ title: "Dark Pact", body: "Once per Turning Point, when performing a Shoot or Fight action, you may re-roll all attack dice. If you do, this operative suffers 1 mortal wound after the action." }],
      uniqueActionTitle: "BLOOD FOR THE BLOOD GOD",
      uniqueActionAp: "AP 1",
      uniqueActionBody: "This operative may immediately perform a free Fight action, but suffers D3 mortal wounds after the action is resolved.",
      keywords: "CHAOS \u00B7 HERETIC ASTARTES \u00B7 LEGIONARY \u00B7 CHAMPION \u00B7 LEADER",
      theme: { ...defaultTheme, headerAccent: "#8b0000", accentBar: "#8b0000", headerBg: "#1a0808" },
    },
  },
  {
    label: "Tyranid",
    icon: "",
    data: {
      title: "TYRANID WARRIOR",
      subtitle: "HIVE FLEET SYNAPSE",
      factionIcon: "",
      stats: { apl: "3", move: '6"', save: "4+", wounds: "15" },
      weapons: [
        { name: "Deathspitter", atk: "5", hit: "3+", dmg: "3/4", wr: 'Range 10"' },
        { name: "Scything Talons", atk: "5", hit: "3+", dmg: "4/5", wr: "Relentless" },
      ],
      equipmentTitle: "TOXIN SACS",
      equipmentBullets: ["Each time this operative strikes in combat with a critical hit, the target suffers 1 additional mortal wound."],
      abilities: [{ title: "Synapse", body: "While a friendly TYRANID operative is within 6\" of this operative, that operative is not affected by the Fearful condition." }],
      uniqueActionTitle: "PSYCHIC SCREAM",
      uniqueActionAp: "AP 1",
      uniqueActionBody: "Select one enemy operative within 6\". That operative suffers D3 mortal wounds and gains the Shocked condition until the end of the Turning Point.",
      keywords: "TYRANID \u00B7 HIVE FLEET \u00B7 WARRIOR \u00B7 SYNAPSE \u00B7 LEADER",
      theme: { ...defaultTheme, headerAccent: "#6b2fa0", accentBar: "#6b2fa0", headerBg: "#120a1e" },
    },
  },
  {
    label: "Ork",
    icon: "",
    data: {
      title: "BOSS NOB",
      subtitle: "ORKS KOMMANDO LEADER",
      factionIcon: "",
      stats: { apl: "3", move: '6"', save: "4+", wounds: "14" },
      weapons: [
        { name: "Slugga", atk: "4", hit: "4+", dmg: "3/4", wr: 'Range 6"' },
        { name: "Power Klaw", atk: "4", hit: "4+", dmg: "7/9", wr: "Brutal" },
      ],
      equipmentTitle: "'ARD PLATE",
      equipmentBullets: ["This operative's Save characteristic is improved by 1 (e.g. 5+ becomes 4+)."],
      abilities: [{ title: "WAAAGH!", body: "Each time a friendly ORKS operative within 6\" of this operative performs a Fight action, you may re-roll one attack dice." }],
      uniqueActionTitle: "GET STUCK IN!",
      uniqueActionAp: "AP 1",
      uniqueActionBody: "Select one friendly ORKS operative within 6\". That operative may immediately perform a free Charge action.",
      keywords: "ORKS \u00B7 KOMMANDO \u00B7 NOB \u00B7 LEADER",
      theme: { ...defaultTheme, headerAccent: "#5a8a2a", accentBar: "#5a8a2a", headerBg: "#1a2010" },
    },
  },
  {
    label: "Tau",
    icon: "",
    data: {
      title: "FIRE WARRIOR SHAS'UI",
      subtitle: "T'AU PATHFINDER LEADER",
      factionIcon: "",
      stats: { apl: "3", move: '6"', save: "4+", wounds: "9" },
      weapons: [
        { name: "Pulse Rifle", atk: "4", hit: "3+", dmg: "4/5", wr: "" },
        { name: "Bonding Knife", atk: "3", hit: "5+", dmg: "2/3", wr: "" },
      ],
      equipmentTitle: "MARKERLIGHT",
      equipmentBullets: ["Once per Turning Point, select one enemy operative Visible to this operative. Until the end of the Turning Point, each time a friendly TAU operative performs a Shoot action targeting that enemy, improve the BS by 1."],
      abilities: [{ title: "For the Greater Good", body: "Each time a friendly TAU operative within 6\" of this operative is the target of a Shoot action, this operative can perform a free Overwatch action." }],
      uniqueActionTitle: "DESIGNATE TARGET",
      uniqueActionAp: "AP 1",
      uniqueActionBody: "Select one enemy operative Visible to this operative. Until the end of the Turning Point, each time a friendly TAU operative performs a Shoot action targeting that enemy, retain one additional normal hit as a successful hit.",
      keywords: "TAU EMPIRE \u00B7 FIRE WARRIOR \u00B7 SHAS'UI \u00B7 LEADER",
      theme: { ...defaultTheme, headerAccent: "#2a7a8a", accentBar: "#2a7a8a", headerBg: "#0e1a20" },
    },
  },
  {
    label: "Necron",
    icon: "",
    data: {
      title: "IMMORTAL DESPOTEK",
      subtitle: "NECRONS WARRIOR",
      factionIcon: "",
      stats: { apl: "3", move: '5"', save: "3+", wounds: "12" },
      weapons: [
        { name: "Gauss Blaster", atk: "4", hit: "3+", dmg: "5/6", wr: "AP1" },
        { name: "Bayonet", atk: "3", hit: "3+", dmg: "3/4", wr: "" },
      ],
      equipmentTitle: "PHASE SHIFTER",
      equipmentBullets: ["This operative has a 5+ invulnerable save."],
      abilities: [{ title: "Reanimation Protocols", body: "At the start of each Turning Point, this operative regains D3 lost wounds." }],
      uniqueActionTitle: "MY WILL BE DONE",
      uniqueActionAp: "AP 1",
      uniqueActionBody: "Select one friendly NECRONS operative within 6\". That operative may immediately perform a free Dash action or gains +1 to its next Shoot action's hit rolls.",
      keywords: "NECRONS \u00B7 IMMORTAL \u00B7 DESPOTEK \u00B7 LEADER",
      theme: { ...defaultTheme, headerAccent: "#2a8a2a", accentBar: "#2a8a2a", headerBg: "#0a1a0a" },
    },
  },
  {
    label: "Drukhari",
    icon: "",
    data: {
      title: "SYBARITE",
      subtitle: "DRUKHARI KABALITE LEADER",
      factionIcon: "",
      stats: { apl: "3", move: '8"', save: "4+", wounds: "10" },
      weapons: [
        { name: "Splinter Rifle", atk: "4", hit: "3+", dmg: "2/3", wr: "Lethal 5+" },
        { name: "Power Sword", atk: "4", hit: "3+", dmg: "5/6", wr: "Lethal 5+" },
      ],
      equipmentTitle: "PHANTASM GRENADE LAUNCHER",
      equipmentBullets: ["Once per battle, select one enemy operative within 6\". That operative suffers D3 mortal wounds and gains the Stunned condition."],
      abilities: [{ title: "Power From Pain", body: "Each time this operative incapacitates an enemy operative, improve this operative's APL by 1 for the rest of the Turning Point." }],
      uniqueActionTitle: "CRUEL DECEPTION",
      uniqueActionAp: "AP 1",
      uniqueActionBody: "This operative may immediately perform a free Fall Back action. After moving, select one enemy operative within 6\": that enemy gains the Stunned condition.",
      keywords: "DRUKHARI \u00B7 KABALITE \u00B7 SYBARITE \u00B7 LEADER",
      theme: { ...defaultTheme, headerAccent: "#5a1a6a", accentBar: "#5a1a6a", headerBg: "#150a1e" },
    },
  },
  {
    label: "Sisters",
    icon: "",
    data: {
      title: "SISTER SUPERIOR",
      subtitle: "ADEPTA SORORITAS LEADER",
      factionIcon: "",
      stats: { apl: "3", move: '6"', save: "3+", wounds: "11" },
      weapons: [
        { name: "Bolt Pistol", atk: "4", hit: "3+", dmg: "3/4", wr: 'Range 8"' },
        { name: "Power Weapon", atk: "5", hit: "3+", dmg: "5/6", wr: "Lethal 5+" },
      ],
      equipmentTitle: "ROSARIUS",
      equipmentBullets: ["This operative has a 4+ invulnerable save."],
      abilities: [{ title: "Acts of Faith", body: "Once per Turning Point, this operative can perform one additional action. That action cannot be the same type as one already performed this activation." }],
      uniqueActionTitle: "DIVINE GUIDANCE",
      uniqueActionAp: "AP 1",
      uniqueActionBody: "Select one friendly ADEPTA SORORITAS operative within 6\". Until the end of the Turning Point, each time that operative performs a Shoot or Fight action, you may re-roll one attack dice.",
      keywords: "IMPERIUM \u00B7 ADEPTA SORORITAS \u00B7 BATTLE SISTER \u00B7 LEADER",
      theme: { ...defaultTheme, headerAccent: "#8a1a2a", accentBar: "#8a1a2a", headerBg: "#1e0a0e" },
    },
  },
  {
    label: "Blank",
    icon: "",
    data: {
      title: "NEW OPERATIVE",
      subtitle: "CUSTOM DATACARD",
      factionIcon: "",
      stats: { apl: "3", move: '6"', save: "4+", wounds: "8" },
      weapons: [{ name: "Weapon", atk: "3", hit: "4+", dmg: "3/4", wr: "" }],
      equipmentTitle: "EQUIPMENT",
      equipmentBullets: ["Equipment description."],
      abilities: [{ title: "Ability", body: "Ability description." }],
      uniqueActionTitle: "UNIQUE ACTION",
      uniqueActionAp: "AP 1",
      uniqueActionBody: "Action description.",
      keywords: "KEYWORD",
      theme: defaultTheme,
    },
  },
];

function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}

function createNewCard(base = {}) {
  return {
    ...deepClone(defaultCard),
    ...deepClone(base),
    id: base.id || uid(),
    theme: { ...defaultTheme, ...(base.theme || {}) },
  };
}

function cn(...items) {
  return items.filter(Boolean).join(" ");
}

function AppCard({ children, className = "", style = {} }) {
  return <div className={cn("rounded-3xl bg-white border border-slate-200 shadow-sm", className)} style={style}>{children}</div>;
}

function AppCardHeader({ children, className = "" }) {
  return <div className={cn("p-5 pb-3", className)}>{children}</div>;
}

function AppCardTitle({ children, className = "" }) {
  return <h2 className={cn("font-bold text-slate-900", className)}>{children}</h2>;
}

function AppCardContent({ children, className = "" }) {
  return <div className={cn("p-5 pt-0", className)}>{children}</div>;
}

function AppButton({ children, className = "", variant = "solid", size = "md", type = "button", ...props }) {
  const base = "inline-flex items-center justify-center gap-2 rounded-2xl font-medium transition border disabled:opacity-50 disabled:cursor-not-allowed";
  const variants = {
    solid: "bg-slate-900 text-white border-slate-900 hover:bg-slate-800",
    outline: "bg-white text-slate-900 border-slate-300 hover:bg-slate-50",
    ghost: "bg-transparent text-slate-700 border-transparent hover:bg-slate-100",
  };
  const sizes = {
    sm: "px-3 py-2 text-sm",
    md: "px-4 py-2.5 text-sm",
    icon: "w-10 h-10",
  };
  return <button type={type} className={cn(base, variants[variant], sizes[size], className)} {...props}>{children}</button>;
}

function AppInput(props) {
  return <input {...props} className={cn("w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-500", props.className)} />;
}

function AppTextarea(props) {
  return <textarea {...props} className={cn("w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-500", props.className)} />;
}

function AppLabel({ children, className = "" }) {
  return <label className={cn("text-xs uppercase tracking-wide text-slate-600", className)}>{children}</label>;
}

function AppBadge({ children, className = "" }) {
  return <span className={cn("inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700", className)}>{children}</span>;
}

function Separator() {
  return <div className="h-px w-full bg-slate-200" />;
}

function Tabs({ tabs, active, onChange }) {
  return (
    <div className="grid w-full grid-cols-5 rounded-2xl bg-slate-200 p-1">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          onClick={() => onChange(tab.value)}
          className={cn(
            "rounded-xl px-3 py-2 text-sm font-medium transition",
            active === tab.value ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:bg-slate-100"
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function KTStatIcon({ type, theme, size = 14 }) {
  const stroke = theme.accentBar || theme.headerAccent;
  const fill = theme.accentBar || theme.headerAccent;
  const common = { strokeWidth: 2.5, stroke, fill: "none", strokeLinecap: "round", strokeLinejoin: "round" };
  return (
    <svg viewBox="0 0 24 24" style={{ width: size, height: size, margin: "0 auto 1px" }}>
      {type === "APL" && (
        <>
          <circle cx="12" cy="12" r="8" {...common} />
          <path d="M12 7v5l3 2" {...common} />
        </>
      )}
      {type === "MOVE" && (
        <>
          <path d="M4 12h10" {...common} />
          <path d="M11 8l4 4-4 4" {...common} />
          <circle cx="6" cy="8" r="2" style={{ fill, stroke }} />
        </>
      )}
      {type === "SAVE" && (
        <>
          <path d="M12 4l7 3v5c0 4.5-3 7-7 8-4-1-7-3.5-7-8V7l7-3z" {...common} />
          <path d="M9 12l2 2 4-4" {...common} />
        </>
      )}
      {type === "WOUNDS" && (
        <path d="M12 20s-6-3.7-6-9a3.5 3.5 0 016-2 3.5 3.5 0 016 2c0 5.3-6 9-6 9z" style={{ fill: "none", stroke, strokeWidth: 2.5 }} />
      )}
    </svg>
  );
}

function StatBox({ label, value, theme, compact, first }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        background: theme.statBg || "#242424",
        borderLeft: first ? "none" : `1px solid ${theme.border || "#2a2a2a"}`,
        padding: compact ? "4px 1px" : "6px 2px",
      }}
    >
      <KTStatIcon type={label} theme={theme} size={compact ? 10 : 14} />
      <div style={{ fontSize: compact ? 5 : 6, fontWeight: 900, letterSpacing: "0.12em", textTransform: "uppercase", color: theme.textMuted || "#888" }}>{label}</div>
      <div style={{ fontSize: compact ? 14 : 18, fontWeight: 900, lineHeight: 1, marginTop: compact ? 1 : 2, color: theme.textLight || "#fff" }}>{value}</div>
    </div>
  );
}

function KTSectionHeader({ children, right, theme, compact }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        color: "white",
        textTransform: "uppercase",
        fontWeight: 900,
        fontSize: compact ? 5 : 7,
        letterSpacing: "0.1em",
        padding: compact ? "1.5px 6px" : "2px 8px",
        paddingRight: (compact ? 12 : 16) + 6,
        background: theme.accentBar || theme.headerAccent,
        position: "relative",
        zIndex: 1,
      }}
    >
      <span>{children}</span>
      {right ? <span>{right}</span> : null}
    </div>
  );
}

function autoFontSize(baseSize, text, threshold = 120) {
  if (!text || text.length <= threshold) return baseSize;
  const ratio = threshold / text.length;
  return Math.max(baseSize * 0.65, baseSize * ratio);
}

function WeaponRuleTag({ text }) {
  if (!text) return null;
  // Build a combined tooltip from all matched rules
  const matched = Object.keys(WEAPON_RULES).filter((k) => text.includes(k));
  const tip = matched.map((k) => `${k}: ${WEAPON_RULES[k]}`).join("\n\n");
  return <span title={tip || undefined}>{text}</span>;
}

function DataCard({ data, compact = false, printable = false, selected = false }) {
  const theme = data.theme || defaultTheme;
  const safePortraitUrl = sanitizePortraitUrl(data.portraitUrl);
  const portraitSrc = safePortraitUrl || PORTRAIT_PLACEHOLDER;
  const zoom = data.portraitZoom || 1;
  const offX = data.portraitOffsetX || 0;
  const offY = data.portraitOffsetY || 0;

  const sz = compact
    ? { title: 8, subtitle: 5, icon: 12, statVal: 14, statLbl: 5, thHead: 5, thCell: 5, body: 5, heading: 6, kw: 4, pad: "4px 6px", secPad: "3px 6px", vertW: 12 }
    : { title: 13, subtitle: 8, icon: 18, statVal: 18, statLbl: 6, thHead: 7, thCell: 7, body: 7, heading: 8, kw: 6, pad: "5px 8px", secPad: "4px 8px", vertW: 16 };

  return (
    <div
      className={`mx-auto overflow-hidden relative ${selected ? "ring-4 ring-orange-500/50" : ""}`}
      style={{
        width: printable ? `${CARD_W_MM}mm` : "100%",
        minHeight: printable ? `${CARD_H_MM}mm` : undefined,
        background: theme.cardBg || theme.panelBg,
        color: theme.textLight || theme.textDark,
        border: `2px solid ${theme.border}`,
        fontFamily: "'Arial Black', Arial, Helvetica, sans-serif",
        borderRadius: compact ? 6 : 8,
      }}
    >
      {/* ── Chevron watermark ── */}
      <div style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "55%",
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 0,
      }}>
        <svg viewBox="0 0 200 200" style={{ width: "100%", height: "100%", opacity: 0.5 }}>
          <path d="M0,50 L100,120 L200,50" fill="none" stroke={theme.chevronColor || "#222"} strokeWidth="35" />
          <path d="M0,95 L100,165 L200,95" fill="none" stroke={theme.chevronColor || "#222"} strokeWidth="35" />
        </svg>
      </div>

      {/* ── Vertical faction strip ── */}
      <div style={{
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        width: sz.vertW,
        background: theme.accentBar || theme.headerAccent,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2,
        overflow: "hidden",
      }}>
        <div style={{
          writingMode: "vertical-rl",
          fontSize: Math.min(compact ? 4.5 : 5.5, (compact ? 4.5 : 5.5) * (20 / Math.max(20, (data.subtitle || "").length))),
          fontWeight: 900,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.85)",
          whiteSpace: "nowrap",
        }}>
          {data.subtitle || ""}
        </div>
      </div>

      {/* ── Accent bar top ── */}
      <div style={{ height: compact ? 3 : 4, background: theme.accentBar || theme.headerAccent, position: "relative", zIndex: 1 }} />

      {/* ── Header ── */}
      <div
        style={{
          background: theme.headerBg || theme.cardBg,
          color: "white",
          padding: sz.pad,
          paddingRight: sz.vertW + 4,
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 4 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: sz.title, fontWeight: 900, letterSpacing: "0.05em", lineHeight: 1.15, textTransform: "uppercase", wordBreak: "break-word" }}>
              {data.title}
            </div>
            <div style={{ fontSize: sz.subtitle, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", marginTop: 2, color: theme.accentBar || theme.headerAccent }}>
              {data.subtitle}
            </div>
          </div>
          <div style={{ fontSize: sz.icon, lineHeight: 1, fontWeight: 900, opacity: 0.9, flexShrink: 0 }}>
            {data.factionIcon && (data.factionIcon.startsWith("data:image/") || data.factionIcon.startsWith("http"))
              ? <img src={sanitizePortraitUrl(data.factionIcon)} alt="Faction" style={{ width: compact ? 16 : 24, height: compact ? 16 : 24, objectFit: "contain" }} crossOrigin="anonymous" />
              : data.factionIcon}
          </div>
        </div>
      </div>

      {/* ── Portrait + Stats ── */}
      <div style={{ display: "grid", gridTemplateColumns: "0.85fr 1.15fr", borderBottom: `1px solid ${theme.border}`, position: "relative", zIndex: 1, marginRight: sz.vertW }}>
        <div style={{ borderRight: `1px solid ${theme.border}` }}>
          <div style={{ aspectRatio: compact ? "0.95" : "1", background: "#222", overflow: "hidden" }}>
            <img
              src={portraitSrc}
              alt="Portrait"
              style={{
                width: "100%",
                height: "100%",
                display: "block",
                objectFit: data.portraitFit || "cover",
                transform: `scale(${zoom}) translate(${offX}%, ${offY}%)`,
                transformOrigin: "center center",
              }}
              crossOrigin="anonymous"
            />
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", background: theme.statBg || "#242424" }}>
          <StatBox label="APL" value={data.stats.apl} theme={theme} compact={compact} first />
          <StatBox label="MOVE" value={data.stats.move} theme={theme} compact={compact} />
          <StatBox label="SAVE" value={data.stats.save} theme={theme} compact={compact} />
          <StatBox label="WOUNDS" value={data.stats.wounds} theme={theme} compact={compact} />
        </div>
      </div>

      {/* ── Weapons section header ── */}
      <KTSectionHeader theme={theme} compact={compact}>WEAPONS</KTSectionHeader>

      {/* ── Weapons Table ── */}
      <div style={{ padding: sz.secPad, background: "transparent", position: "relative", zIndex: 1, marginRight: sz.vertW }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Arial, Helvetica, sans-serif" }}>
          <thead>
            <tr style={{ borderBottom: `1.5px solid ${theme.accentBar || theme.headerAccent}` }}>
              <th style={{ textAlign: "left", fontSize: sz.thHead, fontWeight: 900, textTransform: "uppercase", padding: "2px 0", color: theme.textMuted || "#888" }}>NAME</th>
              <th style={{ textAlign: "center", fontSize: sz.thHead, fontWeight: 900, textTransform: "uppercase", padding: "2px 2px", width: 22, color: theme.textMuted || "#888" }}>ATK</th>
              <th style={{ textAlign: "center", fontSize: sz.thHead, fontWeight: 900, textTransform: "uppercase", padding: "2px 2px", width: 22, color: theme.textMuted || "#888" }}>HIT</th>
              <th style={{ textAlign: "center", fontSize: sz.thHead, fontWeight: 900, textTransform: "uppercase", padding: "2px 2px", width: 26, color: theme.textMuted || "#888" }}>DMG</th>
              <th style={{ textAlign: "left", fontSize: sz.thHead, fontWeight: 900, textTransform: "uppercase", padding: "2px 0", color: theme.textMuted || "#888" }}>WR</th>
            </tr>
          </thead>
          <tbody>
            {data.weapons.map((weapon, idx) => (
              <tr key={idx} style={{ borderBottom: `1px solid ${theme.mutedLine || "#333"}` }}>
                <td style={{ padding: "3px 4px 3px 0", fontSize: sz.thCell, fontWeight: 700, verticalAlign: "top", color: theme.textLight || "#fff" }}>{weapon.name}</td>
                <td style={{ textAlign: "center", fontSize: sz.thCell, verticalAlign: "top", padding: "3px 1px", color: theme.textLight || "#fff" }}>{weapon.atk}</td>
                <td style={{ textAlign: "center", fontSize: sz.thCell, verticalAlign: "top", padding: "3px 1px", color: theme.textLight || "#fff" }}>{weapon.hit}</td>
                <td style={{ textAlign: "center", fontSize: sz.thCell, verticalAlign: "top", padding: "3px 1px", color: theme.textLight || "#fff" }}>{weapon.dmg}</td>
                <td style={{ fontSize: sz.body, verticalAlign: "top", padding: "3px 0 3px 3px", color: theme.textMuted || "#888" }}><WeaponRuleTag text={weapon.wr} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Equipment ── */}
      <KTSectionHeader theme={theme} compact={compact}>EQUIPMENT</KTSectionHeader>
      <div style={{ padding: sz.secPad, background: "transparent", position: "relative", zIndex: 1, marginRight: sz.vertW }}>
        <div style={{ fontWeight: 900, textTransform: "uppercase", fontSize: sz.heading, letterSpacing: "0.03em", color: theme.textLight || "#fff" }}>{data.equipmentTitle}</div>
        {data.equipmentBullets.map((bullet, idx) => (
          <div key={idx} style={{ display: "flex", gap: 3, fontSize: sz.body, marginTop: 2, fontFamily: "Arial, Helvetica, sans-serif" }}>
            <span style={{ fontWeight: 900, color: theme.accentBar || theme.headerAccent }}>•</span>
            <span style={{ color: theme.textMuted || "#ccc" }}>{bullet}</span>
          </div>
        ))}
      </div>

      {/* ── Abilities ── */}
      <KTSectionHeader theme={theme} compact={compact}>ABILITIES</KTSectionHeader>
      <div style={{ padding: sz.secPad, background: "transparent", position: "relative", zIndex: 1, marginRight: sz.vertW }}>
        {data.abilities.map((ability, idx) => (
          <div key={idx} style={{ marginBottom: idx < data.abilities.length - 1 ? 4 : 0 }}>
            <div style={{ fontWeight: 900, textTransform: "uppercase", fontSize: sz.heading, letterSpacing: "0.03em", color: theme.textLight || "#fff" }}>{ability.title}</div>
            <div style={{ fontSize: autoFontSize(sz.body, ability.body), lineHeight: 1.35, fontFamily: "Arial, Helvetica, sans-serif", color: theme.textMuted || "#ccc" }}>{ability.body}</div>
          </div>
        ))}
      </div>

      {/* ── Unique Action ── */}
      <KTSectionHeader theme={theme} right={data.uniqueActionAp} compact={compact}>UNIQUE ACTION</KTSectionHeader>
      <div style={{ padding: sz.secPad, background: "transparent", position: "relative", zIndex: 1, marginRight: sz.vertW }}>
        <div style={{ fontWeight: 900, textTransform: "uppercase", fontSize: sz.heading, letterSpacing: "0.03em", color: theme.textLight || "#fff" }}>{data.uniqueActionTitle}</div>
        <div style={{ fontSize: autoFontSize(sz.body, data.uniqueActionBody), lineHeight: 1.35, marginTop: 2, fontFamily: "Arial, Helvetica, sans-serif", color: theme.textMuted || "#ccc" }}>{data.uniqueActionBody}</div>
      </div>

      {/* ── Keywords Footer ── */}
      <div
        style={{
          padding: compact ? "3px 6px" : "4px 8px",
          paddingRight: sz.vertW + 4,
          textAlign: "center",
          color: theme.textMuted || "#888",
          fontWeight: 900,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontSize: sz.kw,
          background: theme.headerBg || theme.cardBg,
          borderTop: `1px solid ${theme.border}`,
          position: "relative",
          zIndex: 1,
        }}
      >
        {data.keywords}
      </div>

      {/* ── Accent bar bottom ── */}
      <div style={{ height: compact ? 3 : 4, background: theme.accentBar || theme.headerAccent, position: "relative", zIndex: 1 }} />
    </div>
  );
}

function Field({ label, value, onChange, multiline = false }) {
  return (
    <div className="space-y-1">
      <AppLabel>{label}</AppLabel>
      {multiline ? <AppTextarea value={value} onChange={onChange} className="min-h-[96px]" /> : <AppInput value={value} onChange={onChange} />}
    </div>
  );
}

function WeaponSelector({ onSelect }) {
  const [open, setOpen] = useState(false);
  const [faction, setFaction] = useState("All");
  const [search, setSearch] = useState("");
  const [weaponType, setWeaponType] = useState("All");

  const filtered = useMemo(() => {
    return WEAPON_LIBRARY.filter((w) => {
      if (faction !== "All" && w.faction !== faction) return false;
      if (weaponType !== "All" && w.type !== weaponType) return false;
      if (search) {
        const q = search.toLowerCase();
        return w.name.toLowerCase().includes(q) || w.wr.toLowerCase().includes(q);
      }
      return true;
    });
  }, [faction, search, weaponType]);

  const grouped = useMemo(() => {
    const groups = {};
    for (const w of filtered) {
      const key = `${w.faction} — ${w.type}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(w);
    }
    return groups;
  }, [filtered]);

  if (!open) {
    return (
      <AppButton type="button" size="sm" variant="outline" onClick={() => setOpen(true)} className="w-full">
        <Plus className="w-4 h-4" /> Add from Library
      </AppButton>
    );
  }

  return (
    <div className="rounded-xl border border-orange-300 bg-orange-50 p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-bold text-slate-700">Weapon Library</span>
        <button type="button" onClick={() => setOpen(false)} className="text-xs text-slate-500 hover:text-slate-700 font-medium">Close</button>
      </div>

      <AppInput
        placeholder="Search weapons..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="bg-white"
      />

      <div className="flex gap-2">
        <select
          className="flex-1 rounded-md border border-slate-300 px-2 py-1.5 bg-white text-xs"
          value={faction}
          onChange={(e) => setFaction(e.target.value)}
        >
          <option value="All">All Factions</option>
          {WEAPON_FACTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select
          className="flex-1 rounded-md border border-slate-300 px-2 py-1.5 bg-white text-xs"
          value={weaponType}
          onChange={(e) => setWeaponType(e.target.value)}
        >
          <option value="All">All Types</option>
          <option value="Ranged">Ranged</option>
          <option value="Melee">Melee</option>
        </select>
      </div>

      <div className="max-h-64 overflow-y-auto space-y-2 -mx-1 px-1">
        {Object.keys(grouped).length === 0 && (
          <p className="text-xs text-slate-500 text-center py-4">No weapons match your search.</p>
        )}
        {Object.entries(grouped).map(([groupName, weapons]) => (
          <div key={groupName}>
            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1 sticky top-0 bg-orange-50 py-0.5">{groupName}</div>
            {weapons.map((w, i) => (
              <button
                key={`${w.name}-${i}`}
                type="button"
                className="w-full text-left rounded-lg border border-slate-200 bg-white p-2 mb-1 hover:bg-slate-50 hover:border-orange-300 transition group"
                onClick={() => {
                  onSelect({ name: w.name, atk: w.atk, hit: w.hit, dmg: w.dmg, wr: w.wr });
                  setOpen(false);
                  setSearch("");
                }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-bold truncate">{w.name}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 shrink-0">{w.type}</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {w.atk} ATK · {w.hit} · {w.dmg}{w.wr ? ` · ${w.wr}` : ""}
                </div>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function EditableList({ title, items, setItems, renderItem, addItem }) {
  return (
    <AppCard className="rounded-2xl">
      <AppCardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <AppCardTitle className="text-base">{title}</AppCardTitle>
          <AppButton type="button" size="sm" variant="outline" onClick={() => setItems([...items, addItem()])}>
            <Plus className="w-4 h-4 mr-1" /> Add
          </AppButton>
        </div>
      </AppCardHeader>
      <AppCardContent className="space-y-3">
        {items.map((item, idx) => (
          <div key={idx} className="rounded-2xl border p-3 space-y-3 bg-slate-50">
            <div className="flex justify-between items-center">
              <AppBadge>#{idx + 1}</AppBadge>
              <AppButton type="button" variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setItems(items.filter((_, i) => i !== idx))}>
                <Trash2 className="w-4 h-4" />
              </AppButton>
            </div>
            {renderItem(item, idx)}
          </div>
        ))}
      </AppCardContent>
    </AppCard>
  );
}

export default function KillTeamDatacardGenerator() {
  const [cards, setCards] = useState([createNewCard()]);
  const [selectedId, setSelectedId] = useState(null);
  const [sheetSelection, setSheetSelection] = useState([]);
  const [sheetTitle, setSheetTitle] = useState("Kill Team Card Sheet");
  const [hydrated, setHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [shareCopied, setShareCopied] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [showSheet, setShowSheet] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1.5);
  const [mobilePanel, setMobilePanel] = useState("editor"); // "editor" | "preview"
  const [cookieConsent, setCookieConsent] = useState(() => localStorage.getItem("cookie-consent"));
  const [dragIdx, setDragIdx] = useState(null);
  // Undo/Redo
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const pushUndo = useCallback((snapshot) => {
    undoStack.current.push(snapshot);
    if (undoStack.current.length > 50) undoStack.current.shift();
    redoStack.current = [];
  }, []);
  const [, forceRender] = useState(0);
  const fileInputRef = useRef(null);
  const portraitInputRef = useRef(null);
  const factionIconInputRef = useRef(null);
  const previewCardRef = useRef(null);
  const sheetRef = useRef(null);

  // Load from URL hash (shared link) or localStorage
  useEffect(() => {
    const loadFromHash = async () => {
      const hash = window.location.hash;
      if (hash.startsWith("#card=")) {
        try {
          const encoded = hash.slice(6);
          const json = await decompressString(encoded);
          const raw = JSON.parse(json);
          const validated = validateCardData(raw);
          if (validated) {
            const card = { ...createNewCard(validated), id: uid() };
            setCards([card]);
            setSelectedId(card.id);
            setSheetSelection([card.id]);
            // Clear the hash so it doesn't reload on refresh
            window.history.replaceState(null, "", window.location.pathname + window.location.search);
            setHydrated(true);
            return true;
          }
        } catch {}
      }
      return false;
    };

    loadFromHash().then((loaded) => {
      if (!loaded) {
        try {
          const saved = localStorage.getItem(STORAGE_KEY);
          if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed?.cards?.length) {
              const incoming = parsed.cards.map((card) => ({ ...createNewCard(card), id: card.id || uid() }));
              setCards(incoming);
              setSelectedId(parsed.selectedId || incoming[0].id);
              setSheetSelection(parsed.sheetSelection || incoming.map((c) => c.id).slice(0, 9));
              setSheetTitle(parsed.sheetTitle || "Kill Team Card Sheet");
            }
          }
        } catch {}
        setHydrated(true);
      }
    });
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ cards, selectedId, sheetSelection, sheetTitle }));
  }, [cards, selectedId, sheetSelection, sheetTitle, hydrated]);

  useEffect(() => {
    if (!selectedId && cards.length) setSelectedId(cards[0].id);
    if (!sheetSelection.length && cards.length) setSheetSelection(cards.map((c) => c.id).slice(0, 9));
  }, [cards, selectedId, sheetSelection.length]);

  const selectedCard = useMemo(() => cards.find((c) => c.id === selectedId) || cards[0], [cards, selectedId]);

  const updateSelected = (updater) => {
    pushUndo(deepClone(cards));
    setCards((prev) => prev.map((card) => (card.id === selectedCard.id ? updater(deepClone(card)) : card)));
  };

  const undo = () => {
    if (!undoStack.current.length) return;
    redoStack.current.push(deepClone(cards));
    const prev = undoStack.current.pop();
    setCards(prev);
    forceRender((n) => n + 1);
  };

  const redo = () => {
    if (!redoStack.current.length) return;
    undoStack.current.push(deepClone(cards));
    const next = redoStack.current.pop();
    setCards(next);
    forceRender((n) => n + 1);
  };

  const updatePath = (path, value) => {
    updateSelected((card) => {
      const keys = path.split(".");
      let obj = card;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return card;
    });
  };

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.key === "z" && e.shiftKey))) { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  });

  const loadHtml2Canvas = () => import("html2canvas").then((m) => m.default);
  const loadJsPDF = () => import("jspdf").then((m) => m.default);

  const exportCardPng = async () => {
    const node = previewCardRef.current;
    if (!node) return;
    const html2canvas = await loadHtml2Canvas();
    const canvas = await html2canvas(node, { backgroundColor: null, scale: 3, useCORS: true });
    const link = document.createElement("a");
    link.download = `${selectedCard.title.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "kill-team-card"}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const exportCardPdf = async () => {
    const node = previewCardRef.current;
    if (!node) return;
    const [html2canvas, jsPDF] = await Promise.all([loadHtml2Canvas(), loadJsPDF()]);
    const canvas = await html2canvas(node, { backgroundColor: null, scale: 3, useCORS: true });
    const img = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: [CARD_H_MM, CARD_W_MM] });
    pdf.addImage(img, "PNG", 0, 0, CARD_W_MM, CARD_H_MM);
    pdf.save(`${selectedCard.title.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "kill-team-card"}.pdf`);
  };

  const exportSheetPdf = async () => {
    const node = sheetRef.current;
    if (!node) return;
    const [html2canvas, jsPDF] = await Promise.all([loadHtml2Canvas(), loadJsPDF()]);
    const canvas = await html2canvas(node, { backgroundColor: "#ffffff", scale: 2, useCORS: true });
    const img = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    pdf.addImage(img, "PNG", 0, 0, pageW, pageH);
    pdf.save(`${sheetTitle.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "kill-team-sheet"}.pdf`);
  };

  const exportAllPngsZip = async () => {
    const [html2canvas, JSZip] = await Promise.all([
      loadHtml2Canvas(),
      import("jszip").then((m) => m.default),
    ]);
    const zip = new JSZip();
    const tempContainer = document.createElement("div");
    tempContainer.style.position = "absolute";
    tempContainer.style.left = "-9999px";
    document.body.appendChild(tempContainer);
    for (const card of cards) {
      const wrapper = document.createElement("div");
      wrapper.style.width = `${CARD_W_MM}mm`;
      tempContainer.appendChild(wrapper);
      const { createRoot } = await import("react-dom/client");
      const root = createRoot(wrapper);
      await new Promise((resolve) => {
        root.render(<DataCard data={applyModeTheme(card, darkMode)} printable />);
        setTimeout(resolve, 200);
      });
      const canvas = await html2canvas(wrapper, { backgroundColor: null, scale: 3, useCORS: true });
      const blob = await new Promise((r) => canvas.toBlob(r, "image/png"));
      const name = `${card.title.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "card"}-${card.id.slice(0, 6)}.png`;
      zip.file(name, blob);
      root.unmount();
      tempContainer.removeChild(wrapper);
    }
    document.body.removeChild(tempContainer);
    const content = await zip.generateAsync({ type: "blob" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(content);
    link.download = "kill-team-cards.zip";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify({ cards, selectedId, sheetSelection, sheetTitle }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "kill-team-cards.json";
    link.click();
    URL.revokeObjectURL(url);
  };

  const importJson = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        if (!parsed || !Array.isArray(parsed.cards) || !parsed.cards.length) {
          alert("Invalid file: no cards found.");
          return;
        }
        const imported = parsed.cards.slice(0, 50).map((raw) => {
          const validated = validateCardData(raw);
          if (!validated) return null;
          return { ...createNewCard(validated), id: uid() };
        }).filter(Boolean);
        if (!imported.length) {
          alert("No valid cards found in file.");
          return;
        }
        setCards(imported);
        setSelectedId(imported[0].id);
        setSheetSelection(imported.map((c) => c.id).slice(0, 9));
        setSheetTitle(sanitizeString(parsed.sheetTitle, 200) || "Kill Team Card Sheet");
      } catch {
        alert("Could not import JSON file.");
      }
    };
    reader.readAsText(file);
  };

  const shareCard = async () => {
    try {
      const shareable = makeShareableCard(selectedCard);
      const json = JSON.stringify(shareable);
      const encoded = await compressString(json);
      const url = `${window.location.origin}${window.location.pathname}#card=${encoded}`;
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      alert("Could not generate share link.");
    }
  };

  const handlePortraitFile = (file) => {
    if (!file.type.startsWith("image/")) return;
    if (file.size > 10 * 1024 * 1024) {
      alert("Image must be under 10 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      if (result.startsWith("data:image/")) {
        updatePath("portraitUrl", result);
      }
    };
    reader.readAsDataURL(file);
  };

  const onDropPortrait = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) handlePortraitFile(file);
  };

  const toggleSheetCard = (id) => {
    setSheetSelection((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id].slice(0, 9)));
  };

  const duplicateCard = () => {
    const copy = createNewCard(selectedCard);
    copy.title = `${selectedCard.title} COPY`;
    setCards((prev) => [...prev, copy]);
    setSelectedId(copy.id);
  };

  const addBlankCard = () => {
    const card = createNewCard({ title: "NEW OPERATIVE", subtitle: "CUSTOM DATACARD" });
    setCards((prev) => [...prev, card]);
    setSelectedId(card.id);
  };

  const addFromTemplate = (template) => {
    const card = createNewCard(template.data);
    setCards((prev) => [...prev, card]);
    setSelectedId(card.id);
  };

  const deleteSelectedCard = () => {
    if (cards.length === 1) return;
    const next = cards.filter((c) => c.id !== selectedCard.id);
    setCards(next);
    setSelectedId(next[0].id);
    setSheetSelection((prev) => prev.filter((x) => x !== selectedCard.id));
  };

  const stats = [
    ["APL", "stats.apl"],
    ["MOVE", "stats.move"],
    ["SAVE", "stats.save"],
    ["WOUNDS", "stats.wounds"],
  ];

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <style>{`
        @media print {
          body { background: white !important; margin: 0; padding: 0; }
          .no-print { display: none !important; }
          .print-sheet-wrap { display: block !important; break-inside: avoid; }
          @page { size: A4; margin: 10mm; marks: crop; }
        }
      `}</style>

      {/* ── Top toolbar ── */}
      <header className="no-print sticky top-0 z-30 border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div className="max-w-[1600px] mx-auto flex flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3 mr-auto">
            <div>
              <h1 className="text-lg font-black tracking-tight text-slate-900">Kill Team Datacard Generator</h1>
              <p className="text-[11px] text-slate-400 leading-tight -mt-0.5">Create and print custom Warhammer 40K Kill Team datacards</p>
            </div>
            <AppBadge>Builder</AppBadge>
            <a href="/how-to-create-kill-team-datacards.html" target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-orange-600 hover:text-orange-700 underline underline-offset-2">Guide</a>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <AppButton size="sm" variant="ghost" onClick={undo} title="Undo (Ctrl+Z)"><Undo2 className="w-4 h-4" /></AppButton>
            <AppButton size="sm" variant="ghost" onClick={redo} title="Redo (Ctrl+Y)"><Redo2 className="w-4 h-4" /></AppButton>
            <div className="w-px h-6 bg-slate-200 hidden sm:block" />
            <AppButton size="sm" onClick={exportCardPng}><FileImage className="w-4 h-4" /> <span className="hidden sm:inline">PNG</span></AppButton>
            <AppButton size="sm" variant="outline" onClick={exportCardPdf}><Download className="w-4 h-4" /> <span className="hidden sm:inline">Card PDF</span></AppButton>
            <AppButton size="sm" variant="outline" onClick={exportSheetPdf}><Printer className="w-4 h-4" /> <span className="hidden sm:inline">Sheet PDF</span></AppButton>
            <AppButton size="sm" variant="outline" onClick={exportAllPngsZip} title="Export all cards as ZIP"><Package className="w-4 h-4" /> <span className="hidden sm:inline">ZIP All</span></AppButton>
            <AppButton size="sm" variant={shareCopied ? "solid" : "outline"} onClick={shareCard}>
              {shareCopied ? <><Check className="w-4 h-4" /> Copied!</> : <><Share2 className="w-4 h-4" /> Share</>}
            </AppButton>
            <div className="w-px h-6 bg-slate-200 hidden sm:block" />
            <AppButton size="sm" variant="outline" onClick={exportJson}><Save className="w-4 h-4" /> <span className="hidden sm:inline">Export</span></AppButton>
            <AppButton size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}><Upload className="w-4 h-4" /> <span className="hidden sm:inline">Import</span></AppButton>
            <AppButton size="sm" variant="ghost" onClick={() => {
              if (!window.confirm("Reset all datacards? This cannot be undone. Make sure to export a JSON backup first if you have cards you want to keep.")) return;
              localStorage.removeItem(STORAGE_KEY);
              const fresh = createNewCard();
              setCards([fresh]);
              setSelectedId(fresh.id);
              setSheetSelection([fresh.id]);
            }}><RotateCcw className="w-4 h-4" /></AppButton>
          </div>
          <input ref={fileInputRef} type="file" accept="application/json" className="hidden" onChange={importJson} />
        </div>
      </header>

      {/* ── Mobile panel toggle ── */}
      <div className="xl:hidden no-print flex border-b border-slate-200 bg-white">
        <button onClick={() => setMobilePanel("editor")} className={cn("flex-1 py-2.5 text-sm font-bold text-center transition", mobilePanel === "editor" ? "text-orange-600 border-b-2 border-orange-500" : "text-slate-500")}>Editor</button>
        <button onClick={() => setMobilePanel("preview")} className={cn("flex-1 py-2.5 text-sm font-bold text-center transition", mobilePanel === "preview" ? "text-orange-600 border-b-2 border-orange-500" : "text-slate-500")}>Preview</button>
      </div>

      {/* ── Main content ── */}
      <div className="max-w-[1600px] mx-auto grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-0">

        {/* ── Left sidebar: library + editor ── */}
        <aside className={cn("no-print border-r border-slate-200 bg-white xl:h-[calc(100vh-57px)] xl:overflow-y-auto", mobilePanel === "preview" ? "hidden xl:block" : "")}>

          {/* Template picker */}
          <div className="border-b border-slate-200 p-4 space-y-3">
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">New from Template</h2>
            <div className="flex flex-wrap gap-2">
              {TEMPLATES.map((tpl) => (
                <button
                  key={tpl.label}
                  type="button"
                  onClick={() => addFromTemplate(tpl)}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-center hover:bg-slate-100 hover:border-slate-300 transition"
                >
                  <span className="text-[11px] font-semibold text-slate-600">{tpl.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Card library */}
          <div className="border-b border-slate-200 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Card Library</h2>
              <AppButton size="sm" onClick={addBlankCard}><Plus className="w-4 h-4" /> New</AppButton>
            </div>
            <div className="space-y-2">
              {cards.map((card, idx) => (
                <button
                  key={card.id}
                  draggable
                  onDragStart={() => setDragIdx(idx)}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("ring-2", "ring-orange-300"); }}
                  onDragLeave={(e) => { e.currentTarget.classList.remove("ring-2", "ring-orange-300"); }}
                  onDrop={(e) => {
                    e.currentTarget.classList.remove("ring-2", "ring-orange-300");
                    if (dragIdx === null || dragIdx === idx) return;
                    const next = [...cards];
                    const [moved] = next.splice(dragIdx, 1);
                    next.splice(idx, 0, moved);
                    setCards(next);
                    setDragIdx(null);
                  }}
                  onDragEnd={() => setDragIdx(null)}
                  onClick={() => setSelectedId(card.id)}
                  className={cn(
                    "w-full text-left rounded-xl border p-3 transition",
                    selectedId === card.id
                      ? "border-orange-500 bg-orange-50 ring-2 ring-orange-400/30"
                      : "border-slate-200 bg-slate-50 hover:bg-slate-100"
                  )}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <GripVertical className="w-3.5 h-3.5 text-slate-400 shrink-0 cursor-grab" />
                      <div className="min-w-0">
                        <div className="font-bold text-sm uppercase truncate">{card.title}</div>
                        <div className="text-xs text-slate-500 uppercase truncate">{card.subtitle}</div>
                      </div>
                    </div>
                    <label className="flex items-center gap-1.5 text-xs text-slate-500 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" className="rounded" checked={sheetSelection.includes(card.id)} onChange={() => toggleSheetCard(card.id)} />
                      Sheet
                    </label>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <AppButton size="sm" variant="outline" className="flex-1" onClick={duplicateCard}><Copy className="w-4 h-4" /> Duplicate</AppButton>
              <AppButton size="sm" variant="outline" className="flex-1 text-red-600 hover:text-red-700" onClick={deleteSelectedCard}><Trash2 className="w-4 h-4" /> Delete</AppButton>
            </div>
          </div>

          {/* Editor tabs + panels */}
          <div className="p-4 space-y-4">
            <Tabs
              tabs={[
                { value: "general", label: "General" },
                { value: "portrait", label: "Portrait" },
                { value: "weapons", label: "Weapons" },
                { value: "rules", label: "Rules" },
                { value: "sheet", label: "Sheet" },
              ]}
              active={activeTab}
              onChange={setActiveTab}
            />

            {activeTab === "general" && (
              <div className="space-y-4">
                <Field label="Title" value={selectedCard.title} onChange={(e) => updatePath("title", e.target.value)} />
                <Field label="Subtitle" value={selectedCard.subtitle} onChange={(e) => updatePath("subtitle", e.target.value)} />
                <div className="space-y-1">
                  <AppLabel>Faction Icon (text or image)</AppLabel>
                  <div className="flex gap-2 items-center">
                    <AppInput
                      value={selectedCard.factionIcon && selectedCard.factionIcon.startsWith("data:image/") ? "(image)" : selectedCard.factionIcon}
                      onChange={(e) => updatePath("factionIcon", e.target.value)}
                      placeholder="e.g. Ω or upload image"
                      readOnly={selectedCard.factionIcon?.startsWith("data:image/")}
                    />
                    <AppButton size="sm" variant="outline" onClick={() => factionIconInputRef.current?.click()}><Upload className="w-4 h-4" /></AppButton>
                    {selectedCard.factionIcon && (
                      <AppButton size="sm" variant="outline" onClick={() => updatePath("factionIcon", "")}><Trash2 className="w-4 h-4" /></AppButton>
                    )}
                  </div>
                  <input ref={factionIconInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file || !file.type.startsWith("image/")) return;
                    if (file.size > 2 * 1024 * 1024) { alert("Max 2 MB"); return; }
                    const reader = new FileReader();
                    reader.onload = (ev) => updatePath("factionIcon", ev.target.result);
                    reader.readAsDataURL(file);
                    e.target.value = "";
                  }} />
                </div>
                <Separator />
                <div className="grid grid-cols-2 gap-3">
                  {stats.map(([label, path]) => (
                    <Field key={path} label={label} value={path.split(".").reduce((o, k) => o[k], selectedCard)} onChange={(e) => updatePath(path, e.target.value)} />
                  ))}
                </div>
                <Field label="Keywords" value={selectedCard.keywords} onChange={(e) => updatePath("keywords", e.target.value)} />
              </div>
            )}

            {activeTab === "portrait" && (
              <div className="space-y-4">
                <div onDragOver={(e) => e.preventDefault()} onDrop={onDropPortrait} className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-6 text-center">
                  <div className="font-semibold text-sm">Drag and drop an image here</div>
                  <div className="text-xs text-slate-500 mt-1">or pick a file from your computer</div>
                  <div className="text-[10px] text-slate-400 mt-1">Square images work best (e.g. 1000x1000px). Max 5 MB.</div>
                  <div className="mt-4 flex justify-center gap-2">
                    <AppButton size="sm" onClick={() => portraitInputRef.current?.click()}><Upload className="w-4 h-4" /> Upload</AppButton>
                    <AppButton size="sm" variant="outline" onClick={() => updatePath("portraitUrl", "")}><Trash2 className="w-4 h-4" /> Clear</AppButton>
                  </div>
                  <input ref={portraitInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handlePortraitFile(file);
                  }} />
                </div>
                <Field label="Portrait Image URL (optional)" value={selectedCard.portraitUrl} onChange={(e) => {
                  const val = e.target.value;
                  if (!val || sanitizePortraitUrl(val) === val) {
                    updatePath("portraitUrl", val);
                  }
                }} />
                <div className="space-y-1">
                  <AppLabel>Portrait Fit</AppLabel>
                  <select className="w-full rounded-md border border-slate-300 px-3 py-2 bg-white text-sm" value={selectedCard.portraitFit || "cover"} onChange={(e) => updatePath("portraitFit", e.target.value)}>
                    <option value="cover">Cover</option>
                    <option value="contain">Contain</option>
                    <option value="fill">Fill</option>
                  </select>
                </div>

                <Separator />

                {/* Zoom & Position controls */}
                <div className="space-y-3">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500 flex items-center gap-2">
                    <ZoomIn className="w-4 h-4" /> Zoom & Position
                  </h3>
                  <div className="space-y-1">
                    <AppLabel>Zoom ({((selectedCard.portraitZoom || 1) * 100).toFixed(0)}%)</AppLabel>
                    <input
                      type="range"
                      min="1"
                      max="3"
                      step="0.05"
                      value={selectedCard.portraitZoom || 1}
                      onChange={(e) => updatePath("portraitZoom", parseFloat(e.target.value))}
                      className="w-full accent-orange-500"
                    />
                  </div>

                  <div className="space-y-1">
                    <AppLabel className="flex items-center gap-1"><Move className="w-3 h-3" /> Drag to Reposition</AppLabel>
                    <div
                      className="relative w-full aspect-square rounded-xl overflow-hidden border-2 border-slate-300 bg-stone-300 cursor-grab active:cursor-grabbing select-none"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        const rect = e.currentTarget.getBoundingClientRect();
                        const startX = e.clientX;
                        const startY = e.clientY;
                        const startOffX = selectedCard.portraitOffsetX || 0;
                        const startOffY = selectedCard.portraitOffsetY || 0;
                        const currentZoom = selectedCard.portraitZoom || 1;
                        const maxShift = (currentZoom - 1) * 50;

                        const onMove = (ev) => {
                          const dx = ((ev.clientX - startX) / rect.width) * 100;
                          const dy = ((ev.clientY - startY) / rect.height) * 100;
                          updatePath("portraitOffsetX", Math.max(-maxShift, Math.min(maxShift, startOffX + dx)));
                          updatePath("portraitOffsetY", Math.max(-maxShift, Math.min(maxShift, startOffY + dy)));
                        };
                        const onUp = () => {
                          window.removeEventListener("mousemove", onMove);
                          window.removeEventListener("mouseup", onUp);
                        };
                        window.addEventListener("mousemove", onMove);
                        window.addEventListener("mouseup", onUp);
                      }}
                      onTouchStart={(e) => {
                        const touch = e.touches[0];
                        const rect = e.currentTarget.getBoundingClientRect();
                        const startX = touch.clientX;
                        const startY = touch.clientY;
                        const startOffX = selectedCard.portraitOffsetX || 0;
                        const startOffY = selectedCard.portraitOffsetY || 0;
                        const currentZoom = selectedCard.portraitZoom || 1;
                        const maxShift = (currentZoom - 1) * 50;

                        const onMove = (ev) => {
                          const t = ev.touches[0];
                          const dx = ((t.clientX - startX) / rect.width) * 100;
                          const dy = ((t.clientY - startY) / rect.height) * 100;
                          updatePath("portraitOffsetX", Math.max(-maxShift, Math.min(maxShift, startOffX + dx)));
                          updatePath("portraitOffsetY", Math.max(-maxShift, Math.min(maxShift, startOffY + dy)));
                        };
                        const onEnd = () => {
                          window.removeEventListener("touchmove", onMove);
                          window.removeEventListener("touchend", onEnd);
                        };
                        window.addEventListener("touchmove", onMove, { passive: false });
                        window.addEventListener("touchend", onEnd);
                      }}
                    >
                      <img
                        src={sanitizePortraitUrl(selectedCard.portraitUrl) || PORTRAIT_PLACEHOLDER}
                        alt="Position preview"
                        className="w-full h-full pointer-events-none"
                        style={{
                          objectFit: selectedCard.portraitFit || "cover",
                          transform: `scale(${selectedCard.portraitZoom || 1}) translate(${selectedCard.portraitOffsetX || 0}%, ${selectedCard.portraitOffsetY || 0}%)`,
                          transformOrigin: "center center",
                        }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="bg-black/40 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm flex items-center gap-1.5">
                          <Move className="w-3.5 h-3.5" /> Drag to reposition
                        </div>
                      </div>
                    </div>
                  </div>

                  <AppButton
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      updatePath("portraitZoom", 1);
                      updatePath("portraitOffsetX", 0);
                      updatePath("portraitOffsetY", 0);
                    }}
                  >
                    <RotateCcw className="w-4 h-4" /> Reset Position
                  </AppButton>
                </div>
              </div>
            )}

            {activeTab === "weapons" && (
              <div className="space-y-3">
                <WeaponSelector onSelect={(weapon) => {
                  updateSelected((card) => ({ ...card, weapons: [...card.weapons, weapon] }));
                }} />
                <EditableList
                  title="Weapons"
                  items={selectedCard.weapons}
                  setItems={(items) => updateSelected((card) => ({ ...card, weapons: items }))}
                  addItem={() => ({ name: "New Weapon", atk: "4", hit: "3+", dmg: "3/4", wr: "" })}
                  renderItem={(weapon, idx) => (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <Field label="Name" value={weapon.name} onChange={(e) => {
                          const next = [...selectedCard.weapons]; next[idx].name = e.target.value; updatePath("weapons", next);
                        }} />
                      </div>
                      <Field label="ATK" value={weapon.atk} onChange={(e) => {
                        const next = [...selectedCard.weapons]; next[idx].atk = e.target.value; updatePath("weapons", next);
                      }} />
                      <Field label="HIT" value={weapon.hit} onChange={(e) => {
                        const next = [...selectedCard.weapons]; next[idx].hit = e.target.value; updatePath("weapons", next);
                      }} />
                      <Field label="DMG" value={weapon.dmg} onChange={(e) => {
                        const next = [...selectedCard.weapons]; next[idx].dmg = e.target.value; updatePath("weapons", next);
                      }} />
                      <div className="col-span-2">
                        <Field label="WR" value={weapon.wr} onChange={(e) => {
                          const next = [...selectedCard.weapons]; next[idx].wr = e.target.value; updatePath("weapons", next);
                        }} />
                      </div>
                    </div>
                  )}
                />
              </div>
            )}

            {activeTab === "rules" && (
              <div className="space-y-5">
                <div className="space-y-3">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">Equipment</h3>
                  <Field label="Equipment Title" value={selectedCard.equipmentTitle} onChange={(e) => updatePath("equipmentTitle", e.target.value.toUpperCase())} />
                  {selectedCard.equipmentBullets.map((bullet, idx) => (
                    <div key={idx} className="flex gap-2">
                      <AppInput value={bullet} onChange={(e) => {
                        const next = [...selectedCard.equipmentBullets]; next[idx] = e.target.value; updatePath("equipmentBullets", next);
                      }} />
                      <AppButton variant="ghost" size="icon" onClick={() => updatePath("equipmentBullets", selectedCard.equipmentBullets.filter((_, i) => i !== idx))}><Trash2 className="w-4 h-4" /></AppButton>
                    </div>
                  ))}
                  <AppButton size="sm" variant="outline" onClick={() => updatePath("equipmentBullets", [...selectedCard.equipmentBullets, "New bullet"])}><Plus className="w-4 h-4" /> Add Bullet</AppButton>
                </div>

                <Separator />

                <EditableList
                  title="Abilities"
                  items={selectedCard.abilities}
                  setItems={(items) => updatePath("abilities", items)}
                  addItem={() => ({ title: "New Ability", body: "Ability description." })}
                  renderItem={(ability, idx) => (
                    <>
                      <Field label="Title" value={ability.title} onChange={(e) => {
                        const next = [...selectedCard.abilities]; next[idx].title = e.target.value; updatePath("abilities", next);
                      }} />
                      <Field label="Body" multiline value={ability.body} onChange={(e) => {
                        const next = [...selectedCard.abilities]; next[idx].body = e.target.value; updatePath("abilities", next);
                      }} />
                    </>
                  )}
                />

                <Separator />

                <div className="space-y-3">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">Unique Action</h3>
                  <Field label="Title" value={selectedCard.uniqueActionTitle} onChange={(e) => updatePath("uniqueActionTitle", e.target.value)} />
                  <Field label="AP Cost Label" value={selectedCard.uniqueActionAp} onChange={(e) => updatePath("uniqueActionAp", e.target.value)} />
                  <Field label="Body" multiline value={selectedCard.uniqueActionBody} onChange={(e) => updatePath("uniqueActionBody", e.target.value)} />
                </div>

                <Separator />

                <div className="space-y-3">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">Theme Presets</h3>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: "Default", theme: defaultTheme },
                      { label: "Classic Light", theme: lightTheme },
                      { label: "Ultramarine", theme: { ...defaultTheme, headerAccent: "#1a5fc1", accentBar: "#1a5fc1", headerBg: "#0e1520" } },
                      { label: "Death Guard", theme: { ...defaultTheme, headerAccent: "#6b8e23", accentBar: "#6b8e23", headerBg: "#151a10" } },
                      { label: "Blood Angel", theme: { ...defaultTheme, headerAccent: "#cc0000", accentBar: "#cc0000", headerBg: "#1a0505" } },
                      { label: "Xenos Purple", theme: { ...defaultTheme, headerAccent: "#6b2fa0", accentBar: "#6b2fa0", headerBg: "#120a1e" } },
                      { label: "Tau Ochre", theme: { ...defaultTheme, headerAccent: "#2a7a8a", accentBar: "#2a7a8a", headerBg: "#0e1a20" } },
                      { label: "Necron Green", theme: { ...defaultTheme, headerAccent: "#2a8a2a", accentBar: "#2a8a2a", headerBg: "#0a1a0a" } },
                    ].map((preset) => (
                      <button
                        key={preset.label}
                        type="button"
                        onClick={() => updatePath("theme", { ...preset.theme })}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 transition text-xs font-semibold text-slate-600"
                      >
                        <span className="w-3 h-3 rounded-full border border-slate-300" style={{ background: preset.theme.accentBar || preset.theme.headerAccent }} />
                        {preset.label}
                      </button>
                    ))}
                  </div>
                  <Separator />
                  <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">Custom Colors</h3>
                  {[
                    ["Card Background", "cardBg"],
                    ["Header Background", "headerBg"],
                    ["Accent Bar", "accentBar"],
                    ["Stat Background", "statBg"],
                    ["Text Light", "textLight"],
                    ["Text Muted", "textMuted"],
                    ["Chevron Watermark", "chevronColor"],
                    ["Border", "border"],
                    ["Rule Line", "mutedLine"],
                  ].map(([label, key]) => (
                    <div key={key} className="flex items-center gap-3">
                      <div className="w-32 text-sm text-slate-600 shrink-0">{label}</div>
                      <input type="color" value={selectedCard.theme[key]} onChange={(e) => updatePath(`theme.${key}`, e.target.value)} className="w-9 h-9 rounded border border-slate-300 p-0.5 cursor-pointer" />
                      <AppInput value={selectedCard.theme[key]} onChange={(e) => updatePath(`theme.${key}`, e.target.value)} className="font-mono" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "sheet" && (
              <div className="space-y-4">
                <Field label="Sheet Title" value={sheetTitle} onChange={(e) => setSheetTitle(e.target.value)} />
                <p className="text-sm text-slate-500">Select up to 9 cards from the library above using the "Sheet" checkboxes. The PDF export uses an A4 sheet layout.</p>
                <div className="flex gap-2">
                  <AppButton size="sm" className="flex-1" onClick={exportSheetPdf}><Download className="w-4 h-4" /> Export Sheet PDF</AppButton>
                  <AppButton size="sm" variant="outline" className="flex-1" onClick={() => window.print()}><Printer className="w-4 h-4" /> Print</AppButton>
                </div>
              </div>
            )}
          </div>

          <div className="p-4 pt-0">
            <p className="text-xs text-slate-400 text-center">Changes save automatically in your browser.</p>
          </div>
        </aside>

        {/* ── Right panel: live preview + sheet ── */}
        <main className={cn("p-6 xl:h-[calc(100vh-57px)] xl:overflow-y-auto", mobilePanel === "editor" ? "hidden xl:block" : "")}>
          <div className="space-y-8">

            {/* Live preview */}
            <section className="no-print">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Live Preview</h2>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 border border-slate-300 rounded-lg overflow-hidden">
                    <button onClick={() => setPreviewZoom((z) => Math.max(0.5, z - 0.1))} className="px-2 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 transition-colors">−</button>
                    <span className="text-xs font-semibold text-slate-500 w-10 text-center">{Math.round(previewZoom * 100)}%</span>
                    <button onClick={() => setPreviewZoom((z) => Math.min(2, z + 0.1))} className="px-2 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 transition-colors">+</button>
                  </div>
                  <button
                    onClick={() => setDarkMode(!darkMode)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors"
                    style={{
                      background: darkMode ? "#1a1a1a" : "#ebe2d6",
                      color: darkMode ? "#fff" : "#171717",
                      borderColor: darkMode ? "#333" : "#9b8e83",
                    }}
                  >
                    {darkMode ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}
                    {darkMode ? "Dark" : "Light"}
                  </button>
                </div>
              </div>
              <div className="rounded-2xl bg-slate-200 p-6 md:p-8 flex justify-center overflow-auto" style={{ maxHeight: "calc(100vh - 160px)" }}>
                <div ref={previewCardRef} style={{ width: `${CARD_W_MM}mm`, transform: `scale(${previewZoom})`, transformOrigin: "top center", marginBottom: `calc(${CARD_H_MM}mm * ${previewZoom - 1})` }}>
                  <DataCard data={applyModeTheme(selectedCard, darkMode)} selected printable />
                </div>
              </div>
            </section>

            {/* Sheet preview */}
            <section className="print-sheet-wrap">
              <div className="flex items-center justify-between mb-4 no-print">
                <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Sheet Preview</h2>
                <button
                  onClick={() => setShowSheet(!showSheet)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  {showSheet ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  {showSheet ? "Hide" : "Show"}
                </button>
              </div>
              {showSheet && (
                <div className="rounded-2xl bg-slate-200 p-3 overflow-auto no-print" style={{ maxHeight: 360 }}>
                  <div ref={sheetRef} className="mx-auto bg-white p-[10mm] shadow-sm" style={{ width: "210mm", minHeight: "297mm", transform: "scale(0.45)", transformOrigin: "top center" }}>
                    <div className="text-center font-bold text-[16px] mb-[6mm]">{sheetTitle}</div>
                    <div className="grid grid-cols-3" style={{ gap: `${SHEET_GAP_MM}mm` }}>
                      {sheetSelection.map((id) => {
                        const card = cards.find((c) => c.id === id);
                        if (!card) return null;
                        return <DataCard key={id} data={applyModeTheme(card, darkMode)} compact printable />;
                      })}
                    </div>
                  </div>
                </div>
              )}
            </section>

          </div>
        </main>

      </div>
      <footer className="no-print border-t border-slate-200 bg-slate-50 text-center py-4 px-4">
        <p className="text-xs text-slate-500">
          Feature requests or feedback? Email <a href="mailto:contact@killteamgenerator.com" className="font-semibold text-orange-600 hover:text-orange-700 underline underline-offset-2">contact@killteamgenerator.com</a>
          <span className="mx-2">·</span>
          <button onClick={() => { setCookieConsent(null); localStorage.removeItem("cookie-consent"); }} className="text-slate-400 hover:text-slate-600 underline underline-offset-2">Cookie Settings</button>
        </p>
      </footer>

      {/* ── Cookie Consent Banner ── */}
      {!cookieConsent && (
        <div className="no-print fixed bottom-0 left-0 right-0 z-50 bg-slate-900 text-white p-4 shadow-lg border-t border-slate-700">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex-1 text-sm leading-relaxed">
              <p className="font-semibold mb-1">Cookie Notice</p>
              <p className="text-slate-300 text-xs">
                We use cookies to analyse site traffic via Google Analytics. These cookies help us understand how visitors use our site so we can improve it.
                No personal data is shared with third parties for advertising. Your card data is stored locally in your browser only.
                By clicking "Accept", you consent to the use of analytics cookies.
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={() => {
                  localStorage.setItem("cookie-consent", "declined");
                  setCookieConsent("declined");
                }}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800 transition"
              >
                Decline
              </button>
              <button
                onClick={() => {
                  localStorage.setItem("cookie-consent", "accepted");
                  setCookieConsent("accepted");
                  if (window.loadGA) window.loadGA();
                }}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition"
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
